import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, ListGroup, Badge, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import Loader from '../Components/Loader';
import Message from '../Components/Message';
import BackButton from '../Components/BackButton';
import { useGetPayPalClientIdQuery } from '../slices/ordersApiSlice';
import { useGetMyStatementsQuery, usePayStatementOnlineMutation } from '../slices/eomApiSlice';

function formatMonth(month) {
  if (!month) return '—';
  try {
    return new Date(`${month}-01`).toLocaleString('en-CA', { month: 'long', year: 'numeric' });
  } catch {
    return month;
  }
}

const EomStatementPayScreen = () => {
  const { statementId } = useParams();
  const navigate = useNavigate();

  const { data: statementsData, isLoading, error } = useGetMyStatementsQuery();
  const [payStatementOnline, { isLoading: loadingPay }] = usePayStatementOnlineMutation();

  // Find the statement in the list
  const statement = statementsData?.statements?.find((s) => s._id === statementId);

  // PayPal setup (same pattern as OrderScreen)
  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();
  const { data: paypal, isLoading: loadingPayPal } = useGetPayPalClientIdQuery();

  useEffect(() => {
    if (paypal?.clientId && !loadingPayPal) {
      paypalDispatch({
        type: 'resetOptions',
        value: {
          'client-id': paypal.clientId,
          currency: 'CAD',
        },
      });
      paypalDispatch({ type: 'setLoadingStatus', value: 'pending' });
    }
  }, [paypal, loadingPayPal, paypalDispatch]);

  function createPaypalOrder(_data, actions) {
    return actions.order.create({
      purchase_units: [{ amount: { value: String(statement?.total || 0) } }],
    });
  }

  function onApprove(_data, actions) {
    return actions.order.capture().then(async (details) => {
      try {
        await payStatementOnline({ statementId, details }).unwrap();
        toast.success('Payment successful! Your statement is now paid.');
        navigate('/profile');
      } catch (err) {
        toast.error(err?.data?.message || 'Payment failed. Please try again.');
      }
    });
  }

  function onError(err) {
    toast.error(err?.message || 'PayPal error. Please try again.');
  }

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || 'Error loading statements'}</Message>;
  if (!statement) {
    return (
      <Message variant="danger">
        Statement not found. It may have been paid or deleted.{' '}
        <a href="/profile">Back to profile</a>
      </Message>
    );
  }

  if (statement.isPaid) {
    return (
      <>
        <BackButton />
        <Alert variant="success" className="mt-3">
          This statement (<strong>{statement.statementNumber}</strong>) is already paid.
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/profile')}>
          Back to Profile
        </Button>
      </>
    );
  }

  return (
    <>
      <BackButton />
      <Row className="justify-content-md-center mt-3">
        <Col md={7}>
          <h2 className="mb-4">Pay Monthly Statement</h2>

          {/* Statement Summary */}
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <strong>{statement.statementNumber}</strong> — {formatMonth(statement.month)}
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                {(statement.orders || []).map((o, i) => (
                  <ListGroup.Item key={i} className="d-flex justify-content-between">
                    <span>
                      {new Date(o.createdAt).toLocaleDateString('en-CA')}
                      {' — '}
                      {o.orderRef || o.invoice || '—'}
                    </span>
                    <span>${Number(o.total || 0).toFixed(2)}</span>
                  </ListGroup.Item>
                ))}
                <ListGroup.Item className="d-flex justify-content-between fw-bold bg-light">
                  <span>Total Due</span>
                  <span>${Number(statement.total || 0).toFixed(2)} CAD</span>
                </ListGroup.Item>
                {statement.dueDate && (
                  <ListGroup.Item className="text-muted small">
                    Due: {new Date(statement.dueDate).toLocaleDateString('en-CA')}
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>

          {/* PayPal Buttons */}
          <Card className="shadow-sm">
            <Card.Header>Pay Online</Card.Header>
            <Card.Body>
              {loadingPay && <Loader />}
              {!paypal?.enabled ? (
                <Alert variant="warning">
                  Online payment is not available. Please contact us to arrange payment.
                </Alert>
              ) : isPending || loadingPayPal ? (
                <Loader />
              ) : (
                <PayPalButtons
                  createOrder={createPaypalOrder}
                  onApprove={onApprove}
                  onError={onError}
                />
              )}
              <p className="text-muted small mt-3 mb-0">
                Prefer to pay offline? Contact us at{' '}
                <a href="mailto:info@kccparts.ca">info@kccparts.ca</a> to arrange
                payment by cheque or bank transfer.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default EomStatementPayScreen;
